import { getUserLanguage } from '~/helpers/languageHelper';
import { DriListResponse } from '../../DriMultipleProg.tsx/types';
import {
  DamList, DutList, Illumination, IlluminationProg,
} from '../types';

type MassProgDevice = DamList | DutList | IlluminationProg | DriListResponse;

const sortLocation = (dev_1: MassProgDevice, dev_2: MassProgDevice): number => {
  if (dev_1.STATE_NAME == null) return 1;
  if (dev_2.STATE_NAME == null) return -1;
  if (dev_1.STATE_NAME < dev_2.STATE_NAME) {
    return -1;
  }
  if (dev_1.STATE_NAME > dev_2.STATE_NAME) {
    return 1;
  }
  if (dev_1.CITY_NAME == null) return 1;
  if (dev_2.CITY_NAME == null) return -1;
  if (dev_1.CITY_NAME < dev_2.CITY_NAME) {
    return -1;
  }
  if (dev_1.CITY_NAME > dev_2.CITY_NAME) {
    return 1;
  }
  return 2;
};

const sortUnit = (dev1: MassProgDevice, dev2: MassProgDevice): number => {
  if (dev1.UNIT_NAME == null) return 1;
  if (dev2.UNIT_NAME == null) return -1;
  if (dev1.UNIT_NAME < dev2.UNIT_NAME) {
    return -1;
  }
  if (dev1.UNIT_NAME > dev2.UNIT_NAME) {
    return 1;
  }

  return 0;
};

const sortDutId = (
  dev_1: DutList,
  dev_2: DutList,
): number => dev_1.DEV_ID.localeCompare(dev_2.DEV_ID, getUserLanguage(), {
  sensitivity: 'base',
});

const sortDutRoomName = (dut1: DutList, dut2: DutList): number => {
  const dut1NoRoom = !dut1.ROOM_NAME;
  const dut2NoRoom = !dut2.ROOM_NAME;
  if (dut1NoRoom && dut2NoRoom) return 0;
  if (dut1NoRoom) return 1;
  if (dut2NoRoom) return -1;

  return dut1.ROOM_NAME.localeCompare(dut2.ROOM_NAME, getUserLanguage(), { sensitivity: 'base' });
};

const sortDamGroupName = (dam1: DamList, dam2: DamList): number => {
  const dam1NoGroup = !dam1.groupsNames || dam1.groupsNames === '-';
  const dam2NoGroup = !dam2.groupsNames || dam2.groupsNames === '-';
  if (dam1NoGroup && dam2NoGroup) return 0;
  if (dam1NoGroup) return 1;
  if (dam2NoGroup) return -1;
  return dam1.groupsNames.localeCompare(dam2.groupsNames, getUserLanguage(), { sensitivity: 'base' });
};

const sortDamId = (
  dam1: DamList,
  dam2: DamList,
): number => dam1.DAM_ID.localeCompare(dam2.DAM_ID, getUserLanguage(), { sensitivity: 'base' });

export const sortDut = (dut_1: DutList, dut_2: DutList): number => {
  const res = sortLocation(dut_1, dut_2);
  if (res !== 2) return res;

  const resUnit = sortUnit(dut_1, dut_2);
  if (resUnit !== 0) return resUnit;

  const resRoomName = sortDutRoomName(dut_1, dut_2);
  if (resRoomName !== 0) return resRoomName;

  return sortDutId(dut_1, dut_2);
};

export const sortDam = (dam_1: DamList, dam_2: DamList): number => {
  const res = sortLocation(dam_1, dam_2);
  if (res !== 2) return res;

  const resUnit = sortUnit(dam_1, dam_2);
  if (resUnit !== 0) return resUnit;

  const resGroupName = sortDamGroupName(dam_1, dam_2);
  if (resGroupName !== 0) return resGroupName;

  return sortDamId(dam_1, dam_2);
};

export const sortByLocationAndUnit = (dev1: MassProgDevice, dev2: MassProgDevice): number => {
  const res = sortLocation(dev1, dev2);
  if (res !== 2) return res;
  return sortUnit(dev1, dev2);
};

const sortNullableField = (a: string | undefined | null, b: string | undefined | null): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return a.localeCompare(b, getUserLanguage(), { sensitivity: 'base' });
};

export const sortUnitIllumination = (illum1: Illumination, illum2: Illumination): number => {
  const res = sortNullableField(illum1.ILLUMINATION_NAME, illum2.ILLUMINATION_NAME);
  if (res !== 0) return res;

  return illum1.DEVICE_CODE.localeCompare(illum2.DEVICE_CODE, getUserLanguage(), {
    sensitivity: 'base',
  });
};

export const sortUnitDris = (dri1: DriListResponse, dri2: DriListResponse): number => {
  const resMachineName = sortNullableField(dri1.MACHINE_NAME, dri2.MACHINE_NAME);
  if (resMachineName !== 0) return resMachineName;

  return dri1.DRI_ID.localeCompare(dri2.DRI_ID, getUserLanguage(), {
    sensitivity: 'base',
  });
};
